import React, { FC, useEffect, useState } from 'react';
import {
  FunctionField,
  ListContextProvider,
  Pagination,
  useListController,
  useTranslate,
} from 'react-admin';
import { Grid, Link } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

import {
  Drawer,
  SelectInput as SelectInputComponent,
  StatusField as StatusFieldComponent,
  TextField as TextFieldComponent,
} from '~/components';
import { EmptyResultsDatagrid, StatusField } from '~/components/ra';
import { statusLabel } from '~/components/StatusField';
import RaTabbedShowLayout from '~/theme/overrides/RaTabbedShowLayout';
import { Longdash, time } from '~/utils';
import formatByteSize from '~/utils/formatByteSize';
import { perPageDefault, rowsPerPageOptionsDefault } from '~/utils/pagination';
import { PartitionArchivePartition, PartitionedTable } from '~/api/partitionArchive';
import {
  formatNumber,
  formatPartitionFrom,
  formatPartitionTo,
  l,
  partitionsPath,
  statusesNewestFirst,
} from './utils';

const resource = 'partitionArchive/partitions';

// "Any" status: a real option (not an empty value), so the select shows it
const anyStatus = 'ANY';

type Translate = (key: string, options?: any) => string;

interface Props {
  tables: PartitionedTable[];
  table?: string;
  status?: string;
  onTableChange: (table?: string) => void;
  onStatusChange: (status?: string) => void;
}

// the partitions of the selected ARCHIVE table; no list (and no request) until a table is selected
const PartitionArchivePartitions: FC<Props> = (props) => {
  const { tables, table, status, onTableChange, onStatusChange } = props;
  const translate = useTranslate();
  const t: Translate = (key, options) => translate(l(key), options);
  const classes = useStyles();

  const tableSelect = (
    <SelectInputComponent<string>
      label={t('list.filters.table')}
      name='table'
      value={table || ''}
      options={tables.map((partitionedTable) => ({
        value: partitionedTable.id,
        text: partitionedTable.id,
      }))}
      onChange={(event: any) => onTableChange(event.target.value || undefined)}
      className={classes.formControl}
    />
  );

  // both selects are the dashboard's SelectInput in one row (as the other filter bars), so they align
  const statusSelect = (
    <SelectInputComponent<string>
      label={t('list.filters.status')}
      name='status'
      value={status || anyStatus}
      options={[
        { value: anyStatus, text: translate('utils.any') },
        ...statusesNewestFirst.map((partitionStatus) => ({
          value: partitionStatus,
          text: translate(statusLabel(partitionStatus)),
        })),
      ]}
      onChange={(event: any) =>
        onStatusChange(event.target.value === anyStatus ? undefined : event.target.value)
      }
      className={classes.formControl}
    />
  );

  return (
    <div style={RaTabbedShowLayout.content}>
      {table ? (
        <>
          <div className={classes.toolbar}>
            {tableSelect}
            {statusSelect}
          </div>
          <PartitionList key={table} table={table} status={status} t={t} />
        </>
      ) : (
        <>
          <div className={classes.toolbar}>{tableSelect}</div>
          <div className={classes.empty}>{t('list.noTable')}</div>
        </>
      )}
    </div>
  );
};

interface PartitionListProps {
  table: string;
  status?: string;
  t: Translate;
}

const PartitionList: FC<PartitionListProps> = (props) => {
  const { table, status, t } = props;
  const classes = useStyles();
  const byteUnit = (unit: string) => t(`byteUnits.${unit}`);
  const [details, setDetails] = useState<PartitionArchivePartition | null>(null);

  const controllerProps = useListController({
    resource,
    basePath: partitionsPath,
    filter: { table },
    filterDefaultValues: status ? { status } : {},
    perPage: perPageDefault,
    hasCreate: false,
    hasEdit: false,
    hasList: true,
    hasShow: false,
  } as any);
  const { filterValues, displayedFilters, setFilters } = controllerProps;

  // the status is page state (the Status select, an alert): apply it to the list
  useEffect(() => {
    if ((filterValues.status || undefined) !== status) {
      const { status: _status, ...otherFilters } = filterValues; // eslint-disable-line @typescript-eslint/no-unused-vars
      setFilters(status ? { ...otherFilters, status } : otherFilters, displayedFilters);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ListContextProvider value={controllerProps}>
      <Alert severity='info' className={classes.note}>
        {t('list.restoreNote')}
      </Alert>
      <EmptyResultsDatagrid emptyText={t('list.empty')} size='small' selectedIds={null}>
        <FunctionField
          label={t('list.partition')}
          sortable={false}
          render={(record: PartitionArchivePartition) =>
            // only a partition with an archive queue row has details: underlined link, as the table links
            record.enqueuedAt ? (
              <Link
                component='button'
                type='button'
                underline='always'
                onClick={() => setDetails(record)}
                className={classes.link}
              >
                {record.partitionName}
              </Link>
            ) : (
              record.partitionName
            )
          }
        />
        <FunctionField
          label={t('list.period')}
          sortable={false}
          render={(record: PartitionArchivePartition) => (
            <span className={classes.noWrap}>{formatPeriod(record, t)}</span>
          )}
        />
        <StatusField source='status' label={t('list.status')} sortable={false} />
        <FunctionField
          label={t('list.rows')}
          sortable={false}
          textAlign='right'
          render={(record: PartitionArchivePartition) =>
            record.rowCount != null ? formatNumber(record.rowCount) : Longdash
          }
        />
        <FunctionField
          label={t('list.size')}
          sortable={false}
          textAlign='right'
          render={(record: PartitionArchivePartition) =>
            record.byteSize != null ? (
              <span className={classes.noWrap}>{formatByteSize(record.byteSize, byteUnit)}</span>
            ) : (
              Longdash
            )
          }
        />
        <FunctionField
          label={t('list.updatedAt')}
          sortable={false}
          render={(record: PartitionArchivePartition) => (
            <span className={classes.noWrap}>{formatDateTime(record.updatedAt)}</span>
          )}
        />
      </EmptyResultsDatagrid>
      {/* nothing to page: the datagrid's empty text only (react-admin's Pagination adds its own) */}
      {!controllerProps.loading && controllerProps.total > 0 && (
        <Pagination {...controllerProps} rowsPerPageOptions={rowsPerPageOptionsDefault} />
      )}
      <PartitionDetails partition={details} onClose={() => setDetails(null)} t={t} />
    </ListContextProvider>
  );
};

interface PartitionDetailsProps {
  partition: PartitionArchivePartition | null;
  onClose: () => void;
  t: Translate;
}

// the archive details of a partition (its queue row), as the other details drawers (auditLog ActionShow)
const PartitionDetails: FC<PartitionDetailsProps> = (props) => {
  const { partition, onClose, t } = props;
  const translate = useTranslate();
  const byteUnit = (unit: string) => t(`byteUnits.${unit}`);

  return (
    <Drawer
      heading={partition?.partitionName || ''}
      subtitle={partition ? formatPeriod(partition, t) : undefined}
      open={!!partition}
      onClose={onClose}
    >
      {partition && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextFieldComponent label={t('list.status')} copyable={false}>
              <StatusFieldComponent status={partition.status} />
            </TextFieldComponent>
          </Grid>
          <Grid item xs={6}>
            <TextFieldComponent label={t('list.rows')} copyable={false}>
              {partition.rowCount != null ? formatNumber(partition.rowCount) : Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={6}>
            <TextFieldComponent label={t('list.size')} copyable={false}>
              {partition.byteSize != null ? formatByteSize(partition.byteSize, byteUnit) : Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={6}>
            <TextFieldComponent label={t('list.attempts')} copyable={false}>
              {partition.exportAttempts ?? Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={6}>
            <TextFieldComponent label={t('list.attached')} copyable={false}>
              {translate(partition.attached ? 'utils.yes' : 'utils.no')}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent
              label={t('list.objectKey')}
              textWrap
              copyable={partition.objectKey || false}
            >
              {partition.objectKey ? withBreaksAfterSlashes(partition.objectKey) : Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent label={t('list.objectLocation')} textWrap>
              {partition.objectLocation || Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent label={t('list.sha256')} textWrap>
              {partition.sha256 || Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent label={t('list.lastError')} textWrap>
              {partition.lastError || Longdash}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={6}>
            <TextFieldComponent label={t('list.enqueuedAt')} copyable={false}>
              {formatDateTime(partition.enqueuedAt)}
            </TextFieldComponent>
          </Grid>
          <Grid item xs={6}>
            <TextFieldComponent label={t('list.updatedAt')} copyable={false}>
              {formatDateTime(partition.updatedAt)}
            </TextFieldComponent>
          </Grid>
        </Grid>
      )}
    </Drawer>
  );
};

// a long object key wraps after its '/' separators, not inside a segment
const withBreaksAfterSlashes = (value: string) => (
  <>
    {value.split('/').map((segment, index, segments) => (
      <React.Fragment key={index}>
        {segment}
        {index < segments.length - 1 && (
          <>
            /<wbr />
          </>
        )}
      </React.Fragment>
    ))}
  </>
);

const formatPeriod = (partition: PartitionArchivePartition, t: Translate): string =>
  t('periodValue', {
    from: formatPartitionFrom(partition.partitionFrom),
    to: formatPartitionTo(partition.partitionTo),
  });

// one line, in the tenant's time zone, e.g. "19 Oct 2026 12:50" (as LegalConsentsTab, Applications)
const formatDateTime = (value?: string | null): string =>
  value ? time(value).format('lll') : Longdash;

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  formControl: {
    width: 240,
  },
  note: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  empty: {
    fontSize: 14,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  // a button styled as a link (no browser "open in new tab"): the text of the cell, not the button font
  link: {
    font: 'inherit',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
}));

export default PartitionArchivePartitions;
